// FILE: src/services/rosterMetrics.js
//
// Canonical learner-population metrics for the Registered Users roster
// (approved 2026-09-16; see migration 104 and
// claude/activation-audit-and-telemetry-perf-2026-09-16.md).
//
//   Registered  = real accounts (admins + deny-list excluded server-side)
//   Confirmed   = email confirmed (OAuth accounts are always confirmed)
//   Unconfirmed = email never confirmed
//   Started     = confirmed + evidence of entering/starting an assessment,
//                 whether or not an answer persisted
//   Activated   = evidence of answering >=1 question: an answered attempt, a
//                 case-study answer, or a user-attributed first_question_answered
//                 / case_question_answered event where persistence failed.
//                 A session holding only UNANSWERED question slots is NOT activation.
//   Dormant     = confirmed but not activated
//   Questions answered = answered attempts only (repeats included); unanswered
//                 and skipped slots are not counted.
//   Guest answers before registering are flagged (ambiguous) and never counted
//   as activation until guest→account linking is decided.
//
// Pure + DB-free so the definitions are unit-tested in one place.

/**
 * True when a roster row came from the pre-104 RPC (no evidence columns).
 * Those rows cannot be classified under the canonical definitions.
 */
export function isLegacyRow(r) {
  return !!r && typeof r.activated !== 'boolean';
}

/** unconfirmed | activated | started | not_started */
export function rowStatus(r) {
  if (r.email_confirmed === false) return 'unconfirmed';
  if (r.activated) return 'activated';
  if (r.started) return 'started';
  return 'not_started';
}

export const STATUS_LABEL = {
  unconfirmed: 'Unconfirmed',
  not_started: 'Not started',
  started: 'Started',
  activated: 'Activated',
};

export const STATUS_RANK = { unconfirmed: 0, not_started: 1, started: 2, activated: 3 };

/** Normalise one RPC row: numeric coercion, explicit booleans. */
export function normalizeRosterRow(r) {
  const n = (v) => Number(v) || 0;
  const legacy = isLegacyRow(r);
  return {
    ...r,
    attempts: n(r.attempts),
    answered_attempts: legacy ? null : n(r.answered_attempts),
    case_answers: legacy ? null : n(r.case_answers),
    answer_event_count: legacy ? null : n(r.answer_event_count),
    started: legacy ? null : r.started === true,
    activated: legacy ? null : r.activated === true,
    guest_answer_evidence: legacy ? null : r.guest_answer_evidence === true,
    legacy,
  };
}

/** Funnel cards. Returns null metric values when rows are legacy (never guesses). */
export function computeRosterStats(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const total = list.length;
  const unconfirmed = list.filter((r) => r.email_confirmed === false).length;
  const confirmed = total - unconfirmed;
  const legacy = list.some((r) => r.legacy || isLegacyRow(r));
  if (legacy) {
    return {
      total, confirmed, unconfirmed, legacy: true,
      started: null, activated: null, dormant: null, questionsAnswered: null,
      activatedViaEventOnly: null, activatedViaCaseOnly: null, guestOnly: null,
    };
  }
  const conf = list.filter((r) => r.email_confirmed !== false);
  const activated = conf.filter((r) => r.activated).length;
  return {
    total,
    confirmed,
    unconfirmed,
    legacy: false,
    started: conf.filter((r) => r.started || r.activated).length,
    activated,
    dormant: confirmed - activated,
    questionsAnswered: list.reduce((s, r) => s + (Number(r.answered_attempts) || 0), 0),
    activatedViaEventOnly: conf.filter((r) => r.activated && !r.answered_attempts && !r.case_answers).length,
    activatedViaCaseOnly: conf.filter((r) => r.activated && !r.answered_attempts && r.case_answers > 0).length,
    // Answered only as a guest before registering: flagged, not counted.
    guestOnly: conf.filter((r) => r.guest_answer_evidence && !r.activated).length,
  };
}
